import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, FormHelperText } from '@mui/material';
import { useLang } from '~/hooks/useLang';
import useDispatchState from '~/features/Dispatch/stores/DispatchState';
import { useHookstate } from '@hookstate/core';
import LoadingButton from '@mui/lab/LoadingButton';
import FilterAutocompleteAutos from '~/components/FilterAutocompleteAutos';
import getVehiclesDispatch, { getDeviceLastLocation } from '../../services';
import { AutosResponse } from '../../interfaces';
import dayjs from 'dayjs';

type LastLocationInfo = {
  readonly diff: number | null
  readonly ts: string | null
  readonly diffFormatted: string | null
}

type DeviceLocationResponse = {
  readonly data: {
    readonly differenceFormatted?: string | null
    readonly differenceInMinutes?: number | null
    readonly timestamp?: string | null
    readonly created_at?: string | null
    readonly last_location_at?: string | null
    readonly date?: string | null
    readonly datetime?: string | null
    readonly last_seen?: string | null
    readonly device_location?: {
      readonly created_at?: string | null
      readonly timestamp?: string | null
    } | null
  } | null
} | null | undefined


export default function AddVehicleDialog({ open }) {
  const { translate } = useLang();
  const { associateVehicleDispatch, dispatch } = useDispatchState();
  const { id } = dispatch();
  const [vehicleIds, setVehicleIds] = useState<readonly number[]>([]);
  const [options, setOptions] = useState<readonly AutosResponse[]>([]);
  const [lastLocations, setLastLocations] = useState<Record<number, LastLocationInfo>>({});
  const loading = useHookstate<boolean>(false);

  const handleClose = () => {
    open.set(false);
  };

  const action = () => {
    if (vehicleIds.length > 0) {
      loading.set(true);
      associateVehicleDispatch(vehicleIds, loading).finally(() => {
        handleClose();
      });
    }
  };

  const dispatchId = id.get();

  useEffect(() => {
    if (!dispatchId) return;
    let mounted = true;

    getVehiclesDispatch(dispatchId).then(response => {
      if (!mounted) return;
      const opts = response?.data ?? [];
      setOptions(opts);
    });

    return () => { mounted = false; };
  }, [dispatchId]);

  useEffect(() => {
    if (!vehicleIds || vehicleIds.length === 0) {
      setLastLocations({});
      return;
    }

    let mounted = true;
    const extractTimestamp = (res: DeviceLocationResponse): string | null => {
      if (!res?.data) return null;
      const candidates = [
        res.data.timestamp,
        res.data.created_at,
        res.data.last_location_at,
        res.data.date,
        res.data.datetime,
        res.data.last_seen,
        res.data.device_location?.created_at,
        res.data.device_location?.timestamp,
      ];
      for (const candidate of candidates) {
        if (candidate != null) return String(candidate);
      }
      return null;
    };

    Promise.all(
      vehicleIds.map(selId => {
        if (lastLocations[selId] !== undefined) {
          const existing = lastLocations[selId] || { diff: null, ts: null, diffFormatted: null };
          return Promise.resolve({ id: selId, diff: existing.diff ?? null, ts: existing.ts ?? null, diffFormatted: existing.diffFormatted });
        }
        const option = options.find(opt => opt.id === selId);
        const deviceId = option?.id;
        if (!deviceId) return Promise.resolve({ id: selId, diff: null, ts: null, diffFormatted: null });
        return getDeviceLastLocation(deviceId)
          .then(res => ({
            id: selId,
            diffFormatted: (res as DeviceLocationResponse)?.data?.differenceFormatted ?? null,
            diff: (res as DeviceLocationResponse)?.data?.differenceInMinutes ?? null,
            ts: extractTimestamp(res as DeviceLocationResponse),
          }))
          .catch(() => ({ id: selId, diff: null, ts: null, diffFormatted: null }));
      })
    ).then(results => {
      if (!mounted) return;
      const locationMap: Record<number, LastLocationInfo> = {};
      // eslint-disable-next-line functional/immutable-data
      results.forEach(result => { locationMap[result.id] = { diff: result.diff, ts: result.ts, diffFormatted: result.diffFormatted }; });
      setLastLocations(locationMap);
    });

    return () => { mounted = false; };
  }, [vehicleIds, options]); // eslint-disable-line react-hooks/exhaustive-deps

  const statusFor = (minutes: number | null | undefined) => {
    if (minutes == null) return null;
    if (minutes <= 5) return { text: 'Atualizada', color: '#2e7d32' };
    if (minutes <= 10) return { text: 'Atenção', color: '#f57f17' };
    return { text: 'Defasagem', color: '#d32f2f' };
  }

  const selectedStatus = vehicleIds.map(vehicleId => {
    const opt = options.find(item => item.id === vehicleId);
    const name = opt?.name ?? `#${vehicleId}`;
    const diff = lastLocations[vehicleId]?.diff ?? null;
    const ts = lastLocations[vehicleId]?.ts ?? null;
    const status = statusFor(diff);
    const diffFormatted = lastLocations[vehicleId]?.diffFormatted ?? null;
    const locationSuffix = diffFormatted == null ? '' : ` (${diffFormatted})`;
    const formattedLabel = ts && dayjs(ts).isValid()
      ? `${name}: ${dayjs(ts).format('DD/MM/YYYY HH:mm')}${locationSuffix}`
      : `${name}: ${translate('No location info')}`;
    return { id: vehicleId, name, diff, ts, status, formattedLabel };
  });

  return (
    <Dialog open={open.get()} onClose={handleClose} maxWidth={'xs'} fullWidth>
      <DialogTitle variant='h5' minWidth={"320px"}>
        {translate('Add Vehicle')}
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth size='small' sx={{ mt: 1 }}>
          <FilterAutocompleteAutos
            onAutosChange={setVehicleIds}
            autosId={vehicleIds}
            dispatchId={dispatchId}
            externalOptions={options}
          />

          {selectedStatus.length > 0 && (
            <FormHelperText sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {selectedStatus.map(item => (
                <span
                  key={item.id}
                  style={{
                    color: item.status?.color ?? undefined,
                    fontWeight: item.status ? 600 : undefined,
                    display: 'block',
                  }}
                >
                  {item.formattedLabel}
                </span>
              ))}
            </FormHelperText>
          )}
        </FormControl>
      </DialogContent>
      <DialogActions sx={{px: 3, gap: 1}}>
        <Button
          variant='outlined'
          onClick={handleClose}>
          {translate('Cancel')}
        </Button>
        <LoadingButton
          loading={loading.get()}
          variant='contained'
          onClick={action}
          disabled={!vehicleIds || vehicleIds.length === 0}
        >
          {translate('Submit')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}